import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { doctorService } from '@/services/api';
import { useClinic } from '@/contexts/ClinicContext';
import { toast } from 'sonner';
import { formatDoctorName } from '@/lib/utils';
import {
  User,
  Stethoscope,
  Building2,
  Phone,
  Mail,
  Award,
  ShieldCheck,
  Clock,
  Loader2,
  Save,
} from 'lucide-react';

export const SPECIALIZATIONS = [
  'General Medicine',
  'Cardiology',
  'Pediatrics',
  'Orthopedics',
  'Dermatology',
  'Gynecology & Obstetrics',
  'Neurology',
  'ENT (Otolaryngology)',
  'Ophthalmology',
  'Pulmonology',
  'General Surgery',
];

const DoctorEditModal = ({ isOpen, onClose, doctor, onSuccess }) => {
  const { hospitalId, availableClinics, activeClinicId } = useClinic();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    specialization: 'General Medicine',
    qualification: 'MBBS, MD',
    registrationNo: '',
    consultationFee: 500,
    assignedClinics: [],
    dutySchedule: 'Mon-Sat 09:00-17:00',
    status: 'active',
  });

  useEffect(() => {
    if (doctor && isOpen) {
      const cleanName = (doctor.name || '').replace(/^Dr\.\s*/i, '').trim();
      const parts = cleanName.split(' ').filter(Boolean);
      const firstName = doctor.firstName || parts[0] || '';
      const lastName = doctor.lastName || (parts.length > 1 ? parts.slice(1).join(' ') : '');

      setFormData({
        firstName,
        lastName,
        email: doctor.email || '',
        phone: doctor.phone || '',
        specialization: doctor.specialization || 'General Medicine',
        qualification: doctor.qualification || 'MBBS, MD',
        registrationNo: doctor.registrationNo || doctor.licenseNumber || '',
        consultationFee: doctor.consultationFee !== undefined ? doctor.consultationFee : 500,
        assignedClinics: Array.isArray(doctor.assignedClinics) && doctor.assignedClinics.length > 0
          ? doctor.assignedClinics
          : [activeClinicId || availableClinics[0]?.id || '101'],
        dutySchedule: doctor.dutySchedule || 'Mon-Sat 09:00-17:00',
        status: doctor.status || (doctor.isActive === false ? 'inactive' : 'active'),
      });
    }
  }, [doctor, isOpen, activeClinicId, availableClinics]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleClinicToggle = (clinicId) => {
    setFormData((prev) => {
      const current = prev.assignedClinics || [];
      const exists = current.includes(clinicId);
      if (exists) {
        if (current.length === 1) {
          toast.warning('A doctor must be assigned to at least one clinic');
          return prev;
        }
        return { ...prev, assignedClinics: current.filter((id) => id !== clinicId) };
      } else {
        return { ...prev, assignedClinics: [...current, clinicId] };
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      toast.error('First and last names are required');
      return;
    }
    if (!formData.email.trim()) {
      toast.error('Email address is required');
      return;
    }
    if (!formData.registrationNo.trim()) {
      toast.error('MCI / NMC Registration Number is required');
      return;
    }

    setSaving(true);
    try {
      const autoUsername = doctor?.username || (formData.email.split('@')[0].replace(/[^a-zA-Z0-9]/g, '').slice(0, 30) || 'doctor');
      const payload = {
        name: formatDoctorName(`${formData.firstName.trim()} ${formData.lastName.trim()}`),
        username: autoUsername.toLowerCase(),
        email: formData.email.trim().toLowerCase(),
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        phone: formData.phone.trim(),
        specialization: formData.specialization,
        qualification: formData.qualification.trim(),
        licenseNumber: formData.registrationNo.trim(),
        registrationNumber: formData.registrationNo.trim(),
        consultationFee: Number(formData.consultationFee) || 0,
        assignedClinics: formData.assignedClinics.length > 0 ? formData.assignedClinics : [activeClinicId || '101'],
        dutySchedule: formData.dutySchedule.trim(),
        status: formData.status,
        isActive: formData.status === 'active',
      };

      const updated = await doctorService.update(doctor.id, payload, hospitalId);
      toast.success(`${formatDoctorName(`${formData.firstName} ${formData.lastName}`)} updated successfully`);
      if (onSuccess) onSuccess(updated);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Failed to update doctor profile');
    } finally {
      setSaving(false);
    }
  };

  if (!doctor) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold flex items-center gap-2">
            <Stethoscope className="h-5 w-5 text-teal-600" />
            Edit Doctor Profile
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Update clinical details, credentials, contact information, and clinic assignments for {formatDoctorName(doctor)}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 py-2">
          {/* Section 1: Doctor Identity & Contact */}
          <div className="space-y-3">
            <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-1.5 border-b pb-1.5">
              <User className="h-3.5 w-3.5 text-teal-600" /> Personal & Contact Info
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              <div className="space-y-1">
                <Label htmlFor="edit-firstName" className="text-xs font-medium">First Name *</Label>
                <Input
                  id="edit-firstName"
                  value={formData.firstName}
                  onChange={(e) => handleChange('firstName', e.target.value)}
                  placeholder="e.g. Rajesh"
                  required
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="edit-lastName" className="text-xs font-medium">Last Name *</Label>
                <Input
                  id="edit-lastName"
                  value={formData.lastName}
                  onChange={(e) => handleChange('lastName', e.target.value)}
                  placeholder="e.g. Kumar"
                  required
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="edit-email" className="text-xs font-medium">Email Address *</Label>
                <div className="relative">
                  <Mail className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                  <Input
                    id="edit-email"
                    type="email"
                    className="pl-8"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="doctor@hospital.in"
                    required
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label htmlFor="edit-phone" className="text-xs font-medium">Phone Number *</Label>
                <div className="relative">
                  <Phone className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                  <Input
                    id="edit-phone"
                    className="pl-8"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    placeholder="+91 98765 43210"
                    required
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Section 2: Clinical Credentials */}
          <div className="space-y-3">
            <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-1.5 border-b pb-1.5">
              <ShieldCheck className="h-3.5 w-3.5 text-teal-600" /> Medical Credentials & Practice
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              <div className="space-y-1">
                <Label htmlFor="edit-specialization" className="text-xs font-medium">Specialization *</Label>
                <Select
                  value={formData.specialization}
                  onValueChange={(val) => handleChange('specialization', val)}
                >
                  <SelectTrigger id="edit-specialization" className="text-xs">
                    <SelectValue placeholder="Select Specialization" />
                  </SelectTrigger>
                  <SelectContent>
                    {SPECIALIZATIONS.map((spec) => (
                      <SelectItem key={spec} value={spec} className="text-xs">
                        {spec}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <Label htmlFor="edit-qualification" className="text-xs font-medium">Qualifications *</Label>
                <div className="relative">
                  <Award className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                  <Input
                    id="edit-qualification"
                    className="pl-8"
                    value={formData.qualification}
                    onChange={(e) => handleChange('qualification', e.target.value)}
                    placeholder="e.g. MBBS, MD (Medicine)"
                    required
                  />
                </div>
              </div>

              <div className="space-y-1">
                <Label htmlFor="edit-registrationNo" className="text-xs font-medium">MCI / NMC Registration No *</Label>
                <Input
                  id="edit-registrationNo"
                  value={formData.registrationNo}
                  onChange={(e) => handleChange('registrationNo', e.target.value)}
                  placeholder="e.g. MCI/2021/84729"
                  required
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="edit-consultationFee" className="text-xs font-medium">Consultation Fee (₹ INR) *</Label>
                <div className="relative">
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-xs">₹</span>
                  <Input
                    id="edit-consultationFee"
                    type="number"
                    min="0"
                    className="pl-7"
                    value={formData.consultationFee}
                    onChange={(e) => handleChange('consultationFee', e.target.value)}
                    required
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Section 3: Clinic Assignment & Availability */}
          <div className="space-y-3">
            <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-1.5 border-b pb-1.5">
              <Building2 className="h-3.5 w-3.5 text-teal-600" /> Clinic Assignments & Duty
            </h4>
            
            {/* Multi-Clinic Assignment Checkboxes */}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Assigned Clinics (Multi-Select)</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
                {availableClinics?.map((clinic) => {
                  const isChecked = formData.assignedClinics.includes(clinic.id);
                  return (
                    <label
                      key={clinic.id}
                      className="flex items-center gap-2 cursor-pointer p-1.5 rounded hover:bg-white transition-colors"
                    >
                      <Checkbox
                        checked={isChecked}
                        onCheckedChange={() => handleClinicToggle(clinic.id)}
                      />
                      <span className="text-xs text-slate-800 font-medium">
                        {clinic.name}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs pt-1">
              <div className="space-y-1">
                <Label htmlFor="edit-dutySchedule" className="text-xs font-medium">Duty Schedule</Label>
                <div className="relative">
                  <Clock className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                  <Input
                    id="edit-dutySchedule"
                    className="pl-8"
                    value={formData.dutySchedule}
                    onChange={(e) => handleChange('dutySchedule', e.target.value)}
                    placeholder="e.g. Mon-Sat 09:00-17:00"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <Label htmlFor="edit-status" className="text-xs font-medium">Duty Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(val) => handleChange('status', val)}
                >
                  <SelectTrigger id="edit-status" className="text-xs">
                    <SelectValue placeholder="Select Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active" className="text-xs">Active Duty</SelectItem>
                    <SelectItem value="inactive" className="text-xs">Inactive / On Leave</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2 pt-2 border-t">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onClose}
              disabled={saving}
              className="text-xs"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={saving}
              className="bg-teal-600 hover:bg-teal-700 text-white font-bold text-xs"
            >
              {saving ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Saving...
                </>
              ) : (
                <>
                  <Save className="h-3.5 w-3.5 mr-1.5" /> Save Changes
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default DoctorEditModal;

